/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { differenceBy, find, isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { transformAndGenerateHash } from 'common/utils';
import PatientBanner from 'common/modules/PatientBanner';

import { refillOrder, validatePrescriptionForRefill } from 'store/thunks/patientThunks';
import NewPatientAddressButton from '../ShippingTab/NewPatientAddressButton';

const LabelValuePair = ({ label, value, alignItems }) => (
  <Grid item xs={12}>
    <Grid container sx={{ minHeight: 40, alignItems }}>
      <Grid item xs={4}>
        <Typography
          sx={{
            color: (theme) => lighten(theme.palette.text.primary, 0.3),
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography sx={{ fontWeight: 400, fontSize: '0.875rem' }}>{value}</Typography>
      </Grid>
    </Grid>
  </Grid>
);

LabelValuePair.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  alignItems: PropTypes.string,
};

LabelValuePair.defaultProps = {
  alignItems: 'center',
};

const RefillButton = ({ prescriptionUUID, patientId, rxNumber, prescribedQuantity, orders }) => {
  const dispatch = useDispatch();
  const [refillValidationResult, setRefillValidationResult] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isCreateRefillLoading, setIsCreateRefillLoading] = useState(false);
  const [refillProcessed, setRefillProcessed] = useState(false);

  const [patientAddresses, setPatientAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [currentOrderShippingAddress, setCurrentOrderShippingAddress] = useState(null);
  const [initialPatientAddresses, setInitialPatientAddresses] = useState([]);
  const [hasInitializedShippingAddress, setHasInitializedShippingAddress] = useState(false);
  const [unVerifiedAddress, setUnVerifiedAddress] = useState([]);
  const [isManuallyMarkedSelected, setIsManuallyMarkedSelected] = useState(false);

  const orderRecord = find(orders, (orderItem) =>
    orderItem.lineItems?.some((item) => item.prescriptionId === prescriptionUUID)
  );
  const [deliveryInstructions, setDeliveryInstructions] = useState(
    orderRecord?.deliveryInstructions || ''
  );

  const patientData = useSelector(({ patient }) => patient[patientId]);
  const partners = useSelector(({ partner, auth }) => partner[auth?.currentTenantId]);
  const isPartnerSuspended = !!find(
    partners,
    (partnerRecord) => partnerRecord.partnerUuid === orderRecord?.partner?.partnerUuid
  )?.isSuspended;
  const prescription = find(
    patientData?.prescriptions,
    (prescriptionData) => prescriptionData.rxNumber === rxNumber
  );

  const isSplitProcessingState = orderRecord?.lineItems?.find(
    (lineItem) =>
      lineItem.prescriptionId === prescriptionUUID && lineItem.processingState === 'SPLIT'
  );

  const orderShippingAddress = orderRecord?.shippingAddress;
  const patientAddress = patientData.addresses;

  const handleCloseModal = () => setShowModal(false);
  const handleOpenModal = () => setShowModal(true);

  const handleAddressSelectChange = (event) => {
    const selectedPatientAddressId = event.target.value;
    setSelectedAddressId(selectedPatientAddressId);
    setIsManuallyMarkedSelected(false);

    const selectedOrdersAddress = patientAddresses?.find(
      (address) => address.patientAddressId === selectedPatientAddressId
    );

    if (selectedOrdersAddress) {
      const { addressLine1, addressLine2, city, state, zip } = selectedOrdersAddress.address;
      const formattedShippingAddress = {
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        zip,
      };
      setCurrentOrderShippingAddress(formattedShippingAddress);
    }
  };

  const handleExistingAddressCheckboxChange = (value) => {
    const isChecked = value.target.checked;
    setIsManuallyMarkedSelected(isChecked);
  };

  const handleRefillOrder = () => {
    setIsCreateRefillLoading(true);

    const formattedShippingAddress = {
      addressLine1: currentOrderShippingAddress.line1,
      addressLine2: currentOrderShippingAddress.line2,
      addressCity: currentOrderShippingAddress.city,
      addressState: currentOrderShippingAddress.state,
      addressZip: currentOrderShippingAddress.zip,
      ...(isManuallyMarkedSelected && { isManuallyVerified: true }),
    };

    const refillData = {
      patientId,
      rxNumber,
      shippingAddress: formattedShippingAddress,
      ...(deliveryInstructions && { deliveryInstructions }),
    };

    dispatch(refillOrder(refillData))
      .then((response) => {
        if (response) {
          setRefillProcessed(true);
          enqueueSnackbar('Created Order Refill!', { variant: 'success' });
        }
      })
      .finally(() => {
        handleCloseModal();
        setIsLoading(false);
        setIsCreateRefillLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(validatePrescriptionForRefill({ prescriptionUUID })).then((response) => {
      setRefillValidationResult(response.result);
      setValidationErrorMessage(response.reasons.join('. '));

      setIsLoading(false);
    });
  }, [dispatch, prescriptionUUID]);

  // Address initialization useEffect
  useEffect(() => {
    // Skip until the addresses are actually loaded — otherwise the state below is set to
    // undefined. An empty array is a valid loaded state and must still fall through.
    if (!patientAddress) return;

    const unVerifiedAddressIds = patientAddress
      ?.filter((item) => !item.isVerified)
      .map((item) => item.patientAddressId);

    if (unVerifiedAddressIds) {
      setUnVerifiedAddress(unVerifiedAddressIds);
    }

    const formatted = patientAddress?.map((item) => {
      const { addressLine1, addressLine2, city, state, zip } = item.address;
      const fullAddress = [addressLine1, addressLine2, `${city}, ${state} ${zip}`]
        .filter(Boolean)
        .join(', ');

      return { ...item, fullAddress };
    });

    setPatientAddresses(formatted);
    if (!hasInitializedShippingAddress && orderShippingAddress) {
      setHasInitializedShippingAddress(true);
      setInitialPatientAddresses(formatted);
      setCurrentOrderShippingAddress(orderShippingAddress);

      const matchedAddress = patientAddress?.find((add) => {
        const { address } = add;
        const patientAddressString = `${address.addressLine1}${address.addressLine2 || ''}${
          address.city
        }${address.state}${address.zip}`;
        const shippingAddressString = `${orderShippingAddress?.line1 || ''}${
          orderShippingAddress?.line2 || ''
        }${orderShippingAddress?.city || ''}${orderShippingAddress?.state || ''}${
          orderShippingAddress?.zip || ''
        }`;

        const patientAddressHash = transformAndGenerateHash(patientAddressString);
        const orderShippingAddressHash = transformAndGenerateHash(shippingAddressString);

        return orderShippingAddressHash === patientAddressHash;
      });

      if (matchedAddress) {
        if (matchedAddress.isVerified) {
          setSelectedAddressId(matchedAddress.patientAddressId);
        }
      }

      return;
    }

    const newAddedAddress = differenceBy(formatted, initialPatientAddresses, 'patientAddressId');

    if (!isEmpty(newAddedAddress)) {
      const newAddress = newAddedAddress[0];

      if (newAddress.isVerified) {
        setSelectedAddressId(newAddress.patientAddressId);
      }

      const { addressLine1, addressLine2, city, state, zip } = newAddress.address;
      setCurrentOrderShippingAddress({
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        zip,
      });

      setIsManuallyMarkedSelected(false);
    }
  }, [
    hasInitializedShippingAddress,
    orderShippingAddress,
    initialPatientAddresses,
    patientAddress,
  ]);

  const getTooltipMessage = () => {
    if (isPartnerSuspended) {
      return 'This partner is suspended.';
    }
    if (isSplitProcessingState) {
      return 'This prescription was split and canceled It is not the original item so refill cannot be triggered';
    }
    return validationErrorMessage;
  };
  const tooltipMessage = getTooltipMessage();

  return (
    <>
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='Prescriptions-RefillOrderButton-header'
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: { maxWidth: '700px' },
        }}
      >
        <DialogContent sx={{ minWidth: 500 }}>
          <Grid container sx={{ mb: 3 }}>
            <Typography variant='h5' sx={{ fontWeight: 600, color: 'primary.main' }}>
              Confirm Prescription Refill
            </Typography>
          </Grid>

          <Grid container sx={{ mb: 2 }}>
            <PatientBanner mpi={patientId} />
          </Grid>

          <Grid container sx={{ mb: 2 }}>
            <Typography variant='h6' component='h3' sx={{ mb: 1 }}>
              Prescription Details
            </Typography>
          </Grid>

          <Grid
            container
            sx={{
              mb: 3,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Grid item xs={12} sm={6}>
              <LabelValuePair label='Drug Name' value={prescription?.drug || 'N/A'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValuePair label='Strength' value={prescription?.strength || 'N/A'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValuePair
                label='Quantity'
                value={prescribedQuantity || prescription?.quantityPrescribed || 'N/A'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValuePair
                label='Remaining Refills'
                value={prescription?.refillsRemaining || 'N/A'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValuePair
                label='Last Fill Date'
                value={
                  prescription?.lastFillDate
                    ? dayjs(prescription.lastFillDate).format('MM/DD/YYYY')
                    : 'N/A'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValuePair label='Rx Number' value={rxNumber || 'N/A'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelValuePair
                label='Directions (Sig)'
                value={
                  prescription?.sig || prescription?.prescriptionOverride?.sig?.sigText || 'N/A'
                }
              />
            </Grid>
          </Grid>

          <Typography variant='h6' component='h3' sx={{ mt: 3, mb: 2 }}>
            Shipping Address
          </Typography>

          <Grid container justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
            <Grid item>
              <Typography
                variant='body2'
                sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
              >
                Select shipping address for this refill
              </Typography>
            </Grid>
            <Grid item>
              <NewPatientAddressButton mpi={patientId} showDeliveryInstructions={false}>
                Add New Address
              </NewPatientAddressButton>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{
              mb: 2,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Grid item xs={12} md={6}>
              {currentOrderShippingAddress && (
                <Box>
                  <Typography sx={{ fontWeight: '500', mb: 1, color: 'primary.main' }}>
                    Current Address:
                  </Typography>
                  <Typography sx={{ mb: 0.5 }}>{currentOrderShippingAddress.line1}</Typography>
                  {currentOrderShippingAddress.line2 && (
                    <Typography sx={{ mb: 0.5 }}>{currentOrderShippingAddress.line2}</Typography>
                  )}
                  <Typography sx={{ mb: 1 }}>
                    {currentOrderShippingAddress.city}, {currentOrderShippingAddress.state}{' '}
                    {currentOrderShippingAddress.zip}
                  </Typography>

                  {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
                    <Box display='flex' alignItems='center' mt={1}>
                      <WarningAmberIcon sx={{ color: 'warning.main', mr: 0.5, fontSize: 16 }} />
                      <Typography color='warning.main' sx={{ fontSize: 12, fontWeight: 500 }}>
                        Could not verify address
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id='select-existing-address-label'>Select existing address</InputLabel>
                <Select
                  labelId='select-existing-address-label'
                  id='select-existing-address'
                  value={selectedAddressId}
                  label='Select another address'
                  onChange={handleAddressSelectChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: 'auto',
                      },
                    },
                  }}
                >
                  {patientAddresses?.map((address) => (
                    <MenuItem key={address.patientAddressId} value={address.patientAddressId}>
                      <Typography sx={{ whiteSpace: 'pre-line' }}>{address.fullAddress}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {selectedAddressId && unVerifiedAddress.includes(selectedAddressId) && (
            <Box
              sx={{
                borderRadius: '8px',
                p: 2,
                bgcolor: 'warning.light',
                mb: 2,
              }}
            >
              <Typography sx={{ mb: 1, fontWeight: 500, color: 'white' }}>
                Address Verification Required
              </Typography>
              <Typography sx={{ mb: 2, fontSize: '14px', color: 'white' }}>
                We were unable to verify the entered address. Please review the options carefully
                and confirm its accuracy before proceeding.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isManuallyMarkedSelected}
                    onChange={handleExistingAddressCheckboxChange}
                    color='primary'
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>
                    Mark address as verified and continue
                  </Typography>
                }
              />
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' component='h3' sx={{ mb: 1 }}>
            Delivery Instructions
          </Typography>
          <TextField
            id='RefillButton-deliveryInstructions-input'
            label='Delivery Instructions'
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            multiline
            rows={2}
            fullWidth
            inputProps={{ maxLength: 200 }}
            helperText='Optional instructions for the carrier. For internal use only.'
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: 'background.default' }}>
          <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
            Cancel
          </Button>

          <LoadingButton
            loading={isCreateRefillLoading}
            variant='contained'
            type='submit'
            onClick={handleRefillOrder}
            disabled={
              !selectedAddressId ||
              (unVerifiedAddress?.includes(selectedAddressId) && !isManuallyMarkedSelected)
            }
            sx={{ minWidth: 120 }}
          >
            Confirm Refill
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Tooltip title={tooltipMessage} placement='top' arrow>
        <span>
          <LoadingButton
            loading={isLoading}
            variant='contained'
            type='submit'
            disabled={
              refillValidationResult === 'FAILURE' ||
              refillProcessed ||
              isSplitProcessingState ||
              isPartnerSuspended
            }
            onClick={handleOpenModal}
            sx={{
              px: 1,
              minHeight: 30,
              fontSize: '0.75rem',
              lineHeight: 'normal',
              minWidth: '5',
              textTransform: 'none',
            }}
          >
            Refill
          </LoadingButton>
        </span>
      </Tooltip>
    </>
  );
};

RefillButton.propTypes = {
  prescriptionUUID: PropTypes.string.isRequired,
  patientId: PropTypes.string.isRequired,
  rxNumber: PropTypes.string.isRequired,
  prescribedQuantity: PropTypes.string,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      lineItems: PropTypes.arrayOf(
        PropTypes.shape({
          prescriptionId: PropTypes.string,
          processingState: PropTypes.string,
        })
      ),
    })
  ),
};

RefillButton.defaultProps = {
  prescribedQuantity: '',
  orders: [],
};

export default RefillButton;
